import * as stylex from "@stylexjs/stylex";
import { spacing } from "@softmax/design-system/tokens.stylex";
import { useEffect, useRef, useState } from "react";

import { Button } from "@softmax/design-system";
import { MediaThumbnail } from "./media-thumbnail";
import type { CatalogMediaItem } from "./media-types";

type MediaEntry = { readonly key: string; readonly item: CatalogMediaItem };
const stripStep = (96 + 8) * 3;

export function MediaThumbnailStrip({
  entries,
  selectedKey,
  onSelect,
}: {
  readonly entries: readonly MediaEntry[];
  readonly selectedKey: string;
  readonly onSelect: (key: string) => void;
}) {
  const [overflowing, setOverflowing] = useState(false);
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (entries.length < 2) return;

    const strip = stripRef.current;

    if (!strip) return;

    const measure = () =>
      setOverflowing(strip.scrollWidth > strip.clientWidth + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(strip);
    return () => observer.disconnect();
  }, [entries.length]);

  function scrollStrip(direction: 1 | -1) {
    const strip = stripRef.current;

    if (strip)
      strip.scrollTo({
        left: strip.scrollLeft + direction * stripStep,
        behavior: "smooth",
      });
  }
  return (
    <div {...stylex.props(styles.stripRow)}>
      {overflowing ? (
        <Button
          size="sm"
          onClick={() => scrollStrip(-1)}
          aria-label="Scroll thumbnails left"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <path d="m6.5 1-4 4 4 4" fill="none" stroke="currentColor" />
          </svg>
        </Button>
      ) : null}
      <div ref={stripRef} {...stylex.props(styles.strip)}>
        {entries.map(({ key, item }) => {
          // A replay key is not an image URL. Borrow a still when its poster is absent.
          const visual =
            item.kind === "replay" && !item.poster
              ? entries.find((entry) => entry.item.kind !== "replay")?.item
              : item;
          return (
            <MediaThumbnail
              key={key}
              item={item}
              visual={visual}
              selected={key === selectedKey}
              onSelect={() => onSelect(key)}
            />
          );
        })}
      </div>
      {overflowing ? (
        <Button
          size="sm"
          onClick={() => scrollStrip(1)}
          aria-label="Scroll thumbnails right"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
            <path d="m3.5 1 4 4-4 4" fill="none" stroke="currentColor" />
          </svg>
        </Button>
      ) : null}
    </div>
  );
}

const styles = stylex.create({
  stripRow: { display: "flex", alignItems: "center", gap: spacing.space6 },
  strip: {
    display: "flex",
    minWidth: 0,
    flex: 1,
    gap: spacing.space8,
    overflowX: "auto",
  },
});
